import { format } from 'date-fns';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { IncomeStatement } from '../../services/reports';

interface IncomeStatementReportProps {
  data: IncomeStatement | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

export default function IncomeStatementReport({
  data,
  loading,
  error,
  onReload,
}: IncomeStatementReportProps) {
  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-2 text-sm text-slate-600">Loading income statement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={onReload}
              className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const renderPeriod = () => {
    const period = data.period as any;
    if (period && typeof period === 'object' && period !== null && period.start && period.end) {
      try {
        return (
          <>Period: {format(new Date(period.start), 'MMM dd, yyyy')} - {format(new Date(period.end), 'MMM dd, yyyy')}</>
        );
      } catch {
        // Fall through to default
      }
    }
    return <>For the period ending {format(new Date(data.reportDate || new Date()), 'MMMM dd, yyyy')}</>;
  };

  const opExpTotal = data.operatingExpenses?.total || 0;
  const operatingEfficiency = opExpTotal > 0
    ? (((data.operatingIncome || 0) / opExpTotal) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-6 py-8 sm:px-8">
        <div className="text-center border-b border-gray-200 pb-6 mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Income Statement</h2>
          <p className="text-sm text-slate-600 mt-2">{renderPeriod()}</p>
        </div>

        <div className="space-y-8">
          {/* Revenue */}
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600 mr-2" />
              Revenue
            </h3>
            <div className="bg-green-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Bird Sales</span>
                  <span className="font-medium">R{(data.revenue?.birdSales || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Breeding Services</span>
                  <span className="font-medium">R{(data.revenue?.breedingFees || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Consulting Fees</span>
                  <span className="font-medium">R{(data.revenue?.consultingFees || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Feed Sales</span>
                  <span className="font-medium">R{(data.revenue?.other || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Equipment Sales</span>
                  <span className="font-medium">R{(data.revenue?.other || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Other Revenue</span>
                  <span className="font-medium">R{(data.revenue?.other || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-green-300 pt-3 mt-3">
                  <div className="flex justify-between items-center text-xl font-bold text-green-900">
                    <span>Total Revenue</span>
                    <span>R{(data.revenue?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost of Goods Sold */}
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
              <ShoppingCartIcon className="h-6 w-6 text-orange-600 mr-2" />
              Cost of Goods Sold
            </h3>
            <div className="bg-orange-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Feed Costs</span>
                  <span className="font-medium">R{(data.costOfGoodsSold?.feedCosts || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Veterinary Costs</span>
                  <span className="font-medium">R{(data.costOfGoodsSold?.veterinaryCosts || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Breeding Costs</span>
                  <span className="font-medium">R{(data.costOfGoodsSold?.breedingCosts || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Other COGS</span>
                  <span className="font-medium">R{(data.costOfGoodsSold?.other || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-orange-300 pt-3 mt-3">
                  <div className="flex justify-between items-center text-lg font-bold text-orange-900">
                    <span>Total COGS</span>
                    <span>R{(data.costOfGoodsSold?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gross Profit */}
          <div className="bg-blue-100 p-6 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-blue-900">Gross Profit</h3>
                <p className="text-sm text-cyan-700">Margin: {(data.grossProfitMargin || 0).toFixed(1)}%</p>
              </div>
              <span className="text-2xl font-bold text-blue-900">R{(data.grossProfit || 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Operating Expenses */}
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-red-600 mr-2" />
              Operating Expenses
            </h3>
            <div className="bg-red-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Facilities Maintenance</span>
                  <span className="font-medium">R{(data.operatingExpenses?.facilitiesMaintenance || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Utilities</span>
                  <span className="font-medium">R{(data.operatingExpenses?.utilities || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Insurance</span>
                  <span className="font-medium">R{(data.operatingExpenses?.insurance || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Labor</span>
                  <span className="font-medium">R{(data.operatingExpenses?.labor || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Marketing</span>
                  <span className="font-medium">R{(data.operatingExpenses?.marketing || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Administrative</span>
                  <span className="font-medium">R{(data.operatingExpenses?.administrative || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Depreciation</span>
                  <span className="font-medium">R{(data.operatingExpenses?.depreciation || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Other Expenses</span>
                  <span className="font-medium">R{(data.operatingExpenses?.other || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-red-300 pt-3 mt-3">
                  <div className="flex justify-between items-center text-lg font-bold text-red-900">
                    <span>Total Operating Expenses</span>
                    <span>R{(data.operatingExpenses?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Operating Income */}
          <div className="bg-cyan-100 p-6 rounded-lg">
            <div className="flex justify-between items-center text-xl font-bold text-indigo-900">
              <span>Operating Income</span>
              <span>R{(data.operatingIncome || 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Other Income/Expenses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-50/80 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-700">Other Income</span>
                <span className="font-medium text-green-600">R{(data.otherIncome || 0).toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-slate-50/80 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-700">Other Expenses</span>
                <span className="font-medium text-red-600">R{(data.otherExpenses || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Net Income */}
          <div className={`p-6 rounded-lg ${(data.netIncome || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className={`text-2xl font-bold ${(data.netIncome || 0) >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                  Net {(data.netIncome || 0) >= 0 ? 'Income' : 'Loss'}
                </h3>
                <p className={`text-sm ${(data.netIncome || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  Net Margin: {(data.netProfitMargin || 0).toFixed(1)}%
                </p>
              </div>
              <span className={`text-3xl font-bold ${(data.netIncome || 0) >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                R{Math.abs(data.netIncome || 0).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="bg-slate-50/80 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Key Performance Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{(data.grossProfitMargin || 0).toFixed(1)}%</div>
                <div className="text-sm text-slate-600">Gross Profit Margin</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${(data.netProfitMargin || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(data.netProfitMargin || 0).toFixed(1)}%
                </div>
                <div className="text-sm text-slate-600">Net Profit Margin</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-600">{operatingEfficiency}%</div>
                <div className="text-sm text-slate-600">Operating Efficiency</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
